import { useEffect, useState } from 'react';
import type { DemandeImmatriculation } from './api';
import type { Nature } from '../../src/partage/edition-geometrie.js';
import type { UniteAngle } from '../../src/partage/cogo-gisement.js';
import type { EtatTrace, GestesTrace } from './trace';
import { surfaceEnFrancais } from '../../src/partage/libelles';
import { useAnnoncer } from './annonces';

/**
 * CE QUE CHAQUE NATURE PEUT FAIRE — et ce qu'elle ne peut PAS.
 *
 * ⚠️⚠️ SEULE L'EMPRISE A UN CHEMIN DE DÉPÔT, ET L'ÉCRAN LE DIT. Mesuré le
 * 23/09/2026 dans les routes du service :
 *
 *  · `POST /parcelles` accepte une géométrie surfacique — l'emprise se dépose ;
 *  · `POST /releves/:reference/bornes` n'accepte AUCUNE coordonnée : il prend
 *    un `indicePoint` dans les points DÉJÀ mesurés du levé. On ne dessine pas
 *    une borne, on QUALIFIE un point qu'un géomètre a relevé. Offrir ici un
 *    bouton « déposer ces bornes » fabriquerait une pose qui n'a pas eu lieu —
 *    exactement ce que le service refuse par `conflit()` ;
 *  · `ter.voie` n'est écrite par AUCUNE route : les lignes se tracent et se
 *    mesurent, elles ne s'enregistrent pas.
 *
 * Une tuile désactivée qui dit pourquoi coûte infiniment moins cher qu'un
 * bouton qui paraît marcher.
 */
/* ⚠️ La lettre vient de TOUCHES_ATELIER (atelier.tsx) — un raccourci qu'il
   faut deviner n'est employé par personne, il est donc ÉCRIT sur le bouton. */
const NATURES: Array<{ cle: Nature; libelle: string; icone: string; touche: string; depot: string | null }> = [
  { cle: 'surface', libelle: 'Emprise', icone: '⬠', touche: 'E', depot: null },
  {
    cle: 'point', libelle: 'Bornes', icone: '•', touche: 'B',
    depot: 'Les bornes ne se dessinent pas : elles se qualifient sur les points '
      + 'd’un levé déjà déposé. Ce tracé mesure et situe, il n’inscrit rien.',
  },
  {
    cle: 'ligne', libelle: 'Voie', icone: '╱', touche: 'V',
    depot: 'Aucune route du service n’écrit une voie : ce tracé mesure une '
      + 'longueur, il ne l’enregistre pas.',
  },
];

/**
 * Les origines que le serveur accepte — RECOPIÉES DU `creationSchema`.
 *
 * ⚠️ UN ENUM FERMÉ : une valeur inventée ici rendrait 400 sur un tracé
 * parfaitement bon, et le message nommerait le champ sans dire lequel des deux
 * côtés a tort. Les libellés, eux, disent ce que l'origine ENGAGE : c'est la
 * `precision_m` et l'opposabilité qui en dépendent, pas un rangement.
 */
const ORIGINES: Array<[DemandeImmatriculation['origine'], string]> = [
  ['leve_terrain', 'Levé de terrain'],
  ['numerisation_plan', 'Numérisation d\'un plan'],
  ['derive_bati', 'Dérivée du bâti'],
  ['declaration', 'Déclaration'],
  ['import', 'Import'],
];

/**
 * LA BARRE DU TRACÉ — ce que l'agent voit pendant qu'il dessine.
 *
 * ⚠️⚠️ CETTE BARRE AFFICHE TROIS ÉTATS DE VERDICT, JAMAIS DEUX. `acceptable`
 * vaut `true`, `false` ou **`null`** — et `null` ne veut pas dire « ça passe » :
 * il veut dire qu'AUCUNE parcelle active ne borde le tracé, donc qu'il n'y a
 * rien à quoi le comparer. Sur un registre qui compte quatre parcelles actives
 * pour 2,7 millions de géométries, c'est le cas ORDINAIRE, pas l'exception. Le
 * peindre en vert donnerait un feu qu'aucune garde n'a allumé — et c'est
 * exactement le vert obtenu faute d'avoir trouvé quoi contredire.
 *
 * ⚠️ LE TÉMOIN DE POPULATION EST AFFICHÉ, PAS SEULEMENT LE VERDICT. « 0 conflit
 * sur 0 voisine examinée » et « 0 conflit sur 37 voisines examinées » sont deux
 * phrases différentes, et une seule est une bonne nouvelle.
 */
export function BarreTrace(
  { etat, gestes, parcelleDesignee, onModifier, gesteReprise = 'direct' }:
  {
  /** Le geste que le DÉPÔT d'une reprise accomplira — nommé AVANT le clic
   *  (D.1488/D.1499) : 'direct' écrit au registre (projet), 'acte' dépose une
   *  mutation de rectification, 'projet' dépose un projet de levé. */
  gesteReprise?: 'direct' | 'acte' | 'projet';
    etat: EtatTrace; gestes: GestesTrace;
    /** La parcelle cliquée sur la carte — `null` si le clic portait ailleurs. */
    parcelleDesignee?: string | null;
    onModifier?: () => void;
  },
) {
  const actif = etat.mode !== 'inactif';
  const [origine, setOrigine] = useState<DemandeImmatriculation['origine']>('leve_terrain');
  const [motif, setMotif] = useState('');
  const [numero, setNumero] = useState('');
  const [precision, setPrecision] = useState('');
  const natureCourante = NATURES.find((n) => n.cle === etat.nature);
  /**
   * ⚠️ LA VIRGULE DÉCIMALE FRANÇAISE REND `NaN`, ET LE 400 ACCUSERAIT LE TRACÉ.
   * Le champ s'annonce « ex. 0,10 » — c'est la notation du pays — mais
   * `Number('0,10')` vaut `NaN`, que `z.number().positive()` refuse. L'agent
   * verrait « précision invalide » sur une valeur qu'il a écrite correctement.
   * On accepte les deux séparateurs, et `null` signifie « rien de saisi », ce
   * qui est différent de « zéro ».
   */
  const precisionM: number | null = (() => {
    const brut = precision.trim().replace(',', '.');
    if (!brut) return null;
    const n = Number(brut);
    return Number.isFinite(n) && n > 0 ? n : null;
  })();
  const precisionMalSaisie = precision.trim() !== '' && precisionM === null;
  /**
   * MESURER — l'ACTE, pas la chose.
   *
   * ⚠️⚠️ L'OUTIL EXISTAIT DÉJÀ ET SON NOM LE CACHAIT. Le bouton « Voie »
   * trace une polyligne, affiche sa longueur, et dit lui-même qu'il ne
   * l'enregistre pas — c'est exactement un mètre à ruban. Mais un agent qui
   * veut mesurer une distance ne clique pas « Voie » : le bouton nomme une
   * CHOSE du registre, pas le geste qu'il accomplit. « Présent et
   * inatteignable », dans sa variante la plus discrète : présent et mal nommé.
   *
   * ⚠️ C'EST UN ÉTAT D'ÉCRAN, PAS UNE QUATRIÈME NATURE. `Nature` est partagée
   * avec le serveur ; y ajouter « mesure » ferait voyager jusqu'au `z.enum` un
   * mot qui ne désigne rien qu'on dépose. Mesurer, c'est tracer sans déposer.
   */
  const [mesurer, setMesurer] = useState(false);
  /** D.946 — la pose au clavier : distance + gisement depuis le dernier sommet. */
  const [cogoDistance, setCogoDistance] = useState('');
  const [cogoGisement, setCogoGisement] = useState('');
  const [cogoUnite, setCogoUnite] = useState<UniteAngle>('grade');
  const [cogoRefus, setCogoRefus] = useState<string | null>(null);
  /* L'accusé de dépôt passe par la région UNIQUE (D.949) — le texte reste
     lisible en place, l'annonce ne double pas. */
  const annoncer = useAnnoncer();
  const departAnnonce = etat.depose?.identifiant ?? etat.corrige?.identifiant ?? null;
  useEffect(() => {
    if (departAnnonce) annoncer(`Dépôt inscrit : ${departAnnonce}.`);
  }, [departAnnonce, annoncer]);

  /**
   * ⚠️⚠️ UNE DISTANCE ET UNE SURFACE NE SE MESURENT PAS DU MÊME GESTE, et les
   * confondre est le piège que le noyau documente : `aireSigneeM2` sur une
   * polyligne rend l'aire du polygone qu'on obtiendrait EN LA REFERMANT — un
   * nombre plausible, affiché en m², et faux. On ne montre donc JAMAIS les deux
   * pour la même figure : une ligne rend sa longueur, une surface son aire.
   */
  const mesureCourante: 'ligne' | 'surface' = etat.nature === 'surface' ? 'surface' : 'ligne';

  const bloquants = etat.diagnostics.filter((d) => d.gravite === 'bloquant');
  const avertissements = etat.diagnostics.filter((d) => d.gravite === 'avertissement');

  /* Le seuil hectare vit maintenant dans le formateur unique : le garder ici
     recréerait le quatrième rendu qu'on vient de supprimer. */
  const surface = surfaceEnFrancais(etat.aireM2);

  return (
    <div className={`barre-trace${actif ? ' active' : ''}`}>
      <div className="barre-trace-gestes">
        {!actif ? (
          <>
            {/* ⚠️ L'ICÔNE ET LE MOT, JAMAIS L'ICÔNE SEULE. Un rail d'icônes
                muettes oblige à survoler chaque bouton pour savoir ce qu'il
                fait — c'est le défaut des logiciels métier denses, et il coûte
                une formation à chaque nouvel agent. */}
            {NATURES.map((n) => (
              <button key={n.cle} type="button"
                className={`bt-outil${n.cle === 'surface' ? ' bt-principal' : ''}`}
                onClick={() => gestes.demarrer(n.cle)}
                title={`Tracer ${n.libelle.toLowerCase()}`}>
                <span className="bt-icone" aria-hidden="true">{n.icone}</span>
                <span>{n.libelle} <kbd>{n.touche}</kbd></span>
              </button>
            ))}
            <button type="button" className="bt-outil"
              onClick={() => { setMesurer(true); gestes.demarrer('ligne'); }}
              title="Mesurer une distance ou une surface, sans rien déposer">
              <span className="bt-icone" aria-hidden="true">⟷</span>
              <span>Mesurer</span>
            </button>
            {/* ⚠️ LE BOUTON N'APPARAÎT QUE SI UNE PARCELLE EST DÉSIGNÉE. Un
                « Modifier » grisé en permanence se lit comme une panne ;
                absent, il apprend qu'il faut d'abord cliquer une parcelle. */}
            {parcelleDesignee && onModifier && (
              <button type="button" className="bt-outil" onClick={onModifier}
                title={`Reprendre l'emprise de ${parcelleDesignee}`}>
                <span className="bt-icone" aria-hidden="true">✎</span>
                <span>Modifier {parcelleDesignee}</span>
              </button>
            )}
          </>
        ) : (
          <>
            {/* Le raccourci est ÉCRIT sur le bouton : un raccourci qu'il faut
                deviner n'est employé par personne. */}
            {mesurer && (
              <div className="bt-mesure-bascule" role="group" aria-label="Ce qu'on mesure">
                {(['ligne', 'surface'] as const).map((n) => (
                  <button key={n} type="button"
                    className={`bt-outil${mesureCourante === n ? ' bt-principal' : ''}`}
                    onClick={() => gestes.demarrer(n)}
                    aria-pressed={mesureCourante === n}>
                    <span>{n === 'ligne' ? 'Distance' : 'Surface'}</span>
                  </button>
                ))}
              </div>
            )}
            <button type="button" className="bt-outil" onClick={gestes.annuler}
              disabled={!etat.peutAnnuler} title="Annuler le dernier geste (Ctrl+Z)">
              <span className="bt-icone" aria-hidden="true">↶</span>
              <span>Annuler <kbd>⌘Z</kbd></span>
            </button>
            <button type="button" className="bt-outil" onClick={gestes.refaire}
              disabled={!etat.peutRefaire} title="Refaire (Ctrl+Maj+Z)">
              <span className="bt-icone" aria-hidden="true">↷</span>
              <span>Refaire <kbd>⌘⇧Z</kbd></span>
            </button>
            <button type="button" onClick={gestes.orthogonaliser}
              className="bt-outil"
              disabled={etat.nature !== 'surface' || etat.sommets < 4}
              title="Redresser les angles proches de 90°">
              <span className="bt-icone" aria-hidden="true">⊾</span>
              <span>Redresser</span>
            </button>
            <button type="button" className="bt-outil" onClick={gestes.vider}
              disabled={etat.sommets === 0} title="Repartir d'un tracé vide">
              <span className="bt-icone" aria-hidden="true">⌫</span>
              <span>Vider</span>
            </button>
            <button type="button" className="bt-outil bt-sortie"
              onClick={() => { setMesurer(false); gestes.arreter(); }}
              title="Fermer l'outil (Échap)">
              <span className="bt-icone" aria-hidden="true">✕</span>
              <span>Fermer <kbd>Échap</kbd></span>
            </button>
          </>
        )}
      </div>

      {actif && (
        <div className="barre-trace-corps">
          {/* ── D.946 : POSER AU CLAVIER — distance + gisement ────────────
              Le point passe par le MÊME accrochage que la souris ; le refus
              s'affiche, il ne se journalise pas. */}
          <div className="bt-cogo">
            <label htmlFor="bt-cogo-d">Distance (m)</label>
            <input id="bt-cogo-d" inputMode="decimal" value={cogoDistance}
              placeholder="ex. 12,40" onChange={(e) => setCogoDistance(e.target.value)} />
            <label htmlFor="bt-cogo-g">Gisement</label>
            <input id="bt-cogo-g" inputMode="decimal" value={cogoGisement}
              placeholder={cogoUnite === 'grade' ? 'ex. 112,45' : 'ex. 101,2'}
              onChange={(e) => setCogoGisement(e.target.value)} />
            <select aria-label="Unité d'angle" value={cogoUnite}
              onChange={(e) => setCogoUnite(e.target.value as UniteAngle)}>
              <option value="grade">gon</option>
              <option value="degre">degrés</option>
            </select>
            <button type="button" className="bt-outil" onClick={() => {
              const refus = gestes.poserAuGisement(
                Number(cogoGisement.trim().replace(',', '.')),
                cogoUnite,
                Number(cogoDistance.trim().replace(',', '.')));
              setCogoRefus(refus);
              if (refus === null) { setCogoDistance(''); setCogoGisement(''); }
            }}>
              <span>Poser</span>
            </button>
            {cogoRefus && <p className="bt-cogo-refus" role="alert">{cogoRefus}</p>}
          </div>
          {/* ── Les mesures, toujours visibles ─────────────────────────── */}
          <dl className="bt-mesures">
            <div><dt>Sommets</dt><dd>{etat.sommets}</dd></div>
            {/* ⚠️ ON N'AFFICHE PAS UNE SURFACE POUR UNE VOIE. Le noyau rend 0,
                et un « 0,0 m² » à côté d'une ligne se lit « surface nulle »
                plutôt que « cette mesure n'a pas de sens ici ». */}
            {etat.nature === 'surface' && (
              <div><dt>Surface</dt><dd>{etat.sommets >= 3 ? surface : '—'}</dd></div>
            )}
            {etat.nature !== 'point' && (
              <div>
                <dt>{etat.nature === 'ligne' ? 'Longueur' : 'Périmètre'}</dt>
                <dd>{etat.sommets >= 2 ? `${etat.mesureLineaireM.toFixed(1)} m` : '—'}</dd>
              </div>
            )}
            <div>
              <dt>Voisines</dt>
              {/* Le nombre de figures RÉELLEMENT chargées pour l'accrochage :
                  s'accrocher « à rien » et n'avoir rien à quoi s'accrocher se
                  ressemblent à l'écran, et pas en vérité. */}
              <dd>{etat.voisinesChargees}</dd>
            </div>
          </dl>

          {/* ── L'accrochage : le DIRE, sinon il paraît être une imprécision ─ */}
          <p className="bt-accroche">
            {etat.accroche
              ? `Accroché sur ${libelleAccroche(etat.accroche.nature)} à ${etat.accroche.distanceM.toFixed(2)} m`
              : 'Aucun accrochage sur le dernier point.'}
          </p>

          {/* ── Ce que la figure se reproche à elle-même ────────────────── */}
          {bloquants.length > 0 && (
            <ul className="bt-diagnostics bloquant">
              {bloquants.map((d, i) => <li key={`b${i}`}>{d.message}</li>)}
            </ul>
          )}
          {avertissements.length > 0 && (
            <ul className="bt-diagnostics avertissement">
              {avertissements.map((d, i) => <li key={`a${i}`}>{d.message}</li>)}
            </ul>
          )}

          {/* ── Ce que la GARDE DU REGISTRE en dit, pendant le tracé ──────
              ⚠️ SEULEMENT POUR UNE SURFACE. `cad.recouvrements` compare des
              emprises : l'interroger avec une ligne ou un semis rendrait un
              verdict sur une figure que la garde n'est pas faite pour juger. */}
          {etat.nature === 'surface' && (
          <div className={`bt-verdict ${classeVerdict(etat)}`}>
            {!etat.deposable ? (
              <span>Le contrôle d’emprise attend une figure complète (trois sommets au moins).</span>
            ) : etat.verdictEnCours ? (
              <span>Contrôle d’emprise en cours…</span>
            ) : etat.verdict === null ? (
              <span>Emprise non contrôlée pour l’instant.</span>
            ) : etat.verdict.acceptable === null ? (
              <span>
                <strong>Non évaluable.</strong> Aucune parcelle active dans ce territoire
                ({etat.verdict.parcelles_actives_territoire}) : il n’y a rien à quoi comparer
                cette emprise. Ce n’est pas un sol libre.
              </span>
            ) : etat.verdict.acceptable ? (
              <span>
                <strong>Emprise acceptable.</strong> {etat.verdict.voisines_examinees} voisine(s)
                examinée(s), aucun recouvrement au-delà de la tolérance.
              </span>
            ) : (
              <span>
                <strong>Emprise refusée.</strong> {etat.verdict.bloquants.length} recouvrement(s)
                au-delà de la tolérance, sur {etat.verdict.voisines_examinees} voisine(s) examinée(s).
                {etat.verdict.bloquants[0] && (
                  <> Le plus grand : {surfaceEnFrancais(etat.verdict.bloquants[0].surface_recouvrement_m2)}.</>
                )}
              </span>
            )}
          </div>
          )}

          {etat.erreur && <p className="bt-erreur" role="alert">{etat.erreur}</p>}

          {/* ── LE DÉPÔT, ou la raison de son absence ───────────────────── */}
          {mesurer ? (
            /**
             * ⚠️⚠️ MESURER NE DÉPOSE RIEN, ET L'ÉCRAN NE DOIT PAS LAISSER
             * CROIRE LE CONTRAIRE. Un formulaire d'immatriculation sous un
             * mètre à ruban ferait d'une mesure un acte à moitié fait : l'agent
             * chercherait pourquoi il ne peut pas « finir ».
             *
             * ⚠️ ET ON NE MONTRE QU'UNE SEULE GRANDEUR. Le noyau le dit :
             * l'aire d'une polyligne est celle du polygone qu'on obtiendrait en
             * la refermant — un nombre plausible, en m², et faux.
             */
            <p className="bt-mesure">
              {mesureCourante === 'surface' ? (
                etat.sommets >= 3
                  ? <>Surface mesurée : <strong>{surface}</strong> · périmètre{' '}
                    <strong>{etat.mesureLineaireM.toFixed(1)} m</strong> sur {etat.sommets} sommets.</>
                  : <>Posez au moins trois sommets pour obtenir une surface.</>
              ) : (
                etat.sommets >= 2
                  ? <>Distance mesurée : <strong>{etat.mesureLineaireM.toFixed(1)} m</strong>{' '}
                    sur {etat.sommets} points.</>
                  : <>Posez un second point pour obtenir une distance.</>
              )}
              {' '}Rien n'est enregistré.
            </p>
          ) : natureCourante?.depot ? (
            /* ⚠️ PAS DE BOUTON ÉTEINT ICI : UNE PHRASE. Un « Déposer » grisé
               se lit « il manque quelque chose à remplir » et fait chercher
               le champ fautif. Ce qui manque n'est pas dans l'écran, c'est une
               route du service — il faut le DIRE, pas le suggérer. */
            <p className="bt-sans-depot">{natureCourante.depot}</p>
          ) : (
          <form
            className="bt-depot"
            onSubmit={(ev) => {
              ev.preventDefault();
              void gestes.deposer({
                origine,
                motif: motif.trim(),
                ...(numero.trim() ? { numeroCadastral: numero.trim() } : {}),
                ...(precisionM !== null ? { precisionM } : {}),
              });
            }}
          >
            <label>
              <span>Origine de la donnée</span>
              <select value={origine} onChange={(ev) => setOrigine(ev.target.value as DemandeImmatriculation['origine'])}>
                {ORIGINES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </label>
            <label>
              <span>Motif <em>(obligatoire, 3 caractères au moins)</em></span>
              <input value={motif} onChange={(ev) => setMotif(ev.target.value)}
                placeholder="Ce que cette immatriculation constate" maxLength={500} />
            </label>
            <div className="bt-depot-paire">
              {/* ⚠️ RETIRÉ PENDANT UNE CORRECTION, PAS GRISÉ. Une correction
                  déplace un contour ; elle ne renumérote pas la parcelle, et
                  le serveur ne lit pas ce champ sur ce chemin. Le laisser
                  saisissable ferait taper une valeur que zod SUPPRIME en
                  silence — l'agent croirait avoir renuméroté. */}
              {!etat.corrige && (
              <label>
                <span>N° cadastral <em>(facultatif)</em></span>
                <input value={numero} onChange={(ev) => setNumero(ev.target.value)} maxLength={80} />
              </label>
              )}
              <label>
                <span>Précision (m) <em>(facultatif)</em></span>
                <input value={precision} onChange={(ev) => setPrecision(ev.target.value)}
                  inputMode="decimal" placeholder="ex. 0,10"
                  aria-invalid={precisionMalSaisie || undefined} />
              </label>
            </div>
            <button type="submit" className="bt-deposer"
              disabled={!etat.deposable || etat.depotEnCours || motif.trim().length < 3 || precisionMalSaisie}>
              {etat.depotEnCours
                ? (etat.corrige ? 'Correction en cours…' : 'Dépôt en cours…')
                : (etat.corrige
                  ? (gesteReprise === 'acte'
                    ? `Déposer une mutation de rectification — ${etat.corrige.identifiant}`
                    : gesteReprise === 'projet'
                      ? `Déposer un projet de levé — ${etat.corrige.identifiant}`
                      : `Corriger l'emprise de ${etat.corrige.identifiant}`)
                  : 'Déposer au registre')}
            </button>
            {precisionMalSaisie && (
              <p className="bt-erreur" role="alert">
                La précision doit être un nombre strictement positif (virgule ou point).
              </p>
            )}
            {/* ⚠️ DIRE CE QUE LE DÉPÔT NE FAIT PAS. Une parcelle déposée naît
                en « projet » : elle ne s'oppose à personne tant qu'un acte ne
                l'inscrit pas au plan. Un agent qui croirait avoir opposé son
                tracé au monde se tromperait d'un cran entier de procédure. */}
            <p className="bt-aide">
              {etat.corrige
                ? <>La correction réécrit le contour de <strong>{etat.corrige.identifiant}</strong>,
                  qui reste en « projet ». Seul un projet se corrige ainsi : une limite portée au
                  plan se déplace par un levé, et si elle porte des droits, par une mutation de
                  rectification.</>
                : <>Le dépôt inscrit la parcelle en <strong>projet</strong> : elle ne s’oppose à
                  personne tant qu’elle n’est pas portée au plan cadastral.</>}
            </p>
          </form>
          )}

          {etat.depose && (
            /* ⚠️ DEUX PHRASES, PARCE QUE DEUX ACTES. « Inscrite » sur une
               correction laisserait croire qu'une parcelle de plus est née —
               c'est exactement ce que le défaut du 23/09 produisait vraiment. */
            <p className="bt-depose">
              {etat.depose.acte === 'acte_depose' ? (
                /* ⚠️ Un ACTE est déposé, pas appliqué : la mutation attend la
                   validation d'un tiers, puis son effet corrige le contour. */
                <>Mutation <strong>{etat.depose.identifiant}</strong> déposée
                  ({etat.depose.statut}). Le registre n'a <strong>pas</strong> été
                  modifié : un tiers la valide, puis l'acte s'applique.</>
              ) : etat.depose.acte === 'proposition' ? (
                /* ⚠️ LE REGISTRE N'A PAS BOUGÉ, et la phrase le dit : un
                   projet de levé attend la décision d'un tiers. */
                <>Projet de levé <strong>{etat.depose.identifiant}</strong> déposé
                  ({etat.depose.statut}). Le registre n'a <strong>pas</strong> été
                  modifié : un tiers décide.</>
              ) : etat.depose.acte === 'correction' ? (
                <>Emprise de <strong>{etat.depose.identifiant}</strong> corrigée
                  ({etat.depose.statut}).
                  {etat.depose.ecartSurfacePct !== null && (
                    <> Surface&nbsp;: {etat.depose.ecartSurfacePct >= 0 ? '+' : ''}
                      {etat.depose.ecartSurfacePct.toLocaleString('fr-FR')} %.</>
                  )} La carte a été rafraîchie.</>
              ) : (
                <>Parcelle <strong>{etat.depose.identifiant}</strong> inscrite en
                  « {etat.depose.statut} ». La carte a été rafraîchie.</>
              )}
            </p>
          )}

          {/* ⚠️ LES GESTES SONT ÉCRITS. Un éditeur dont on doit deviner que
              « alt + clic supprime » est un éditeur que personne n'utilise
              au-delà de ce que la barre montre. */}
          <p className="bt-aide">
            Clic : poser un sommet · Glisser un point : le déplacer ·
            Clic sur un milieu de côté : insérer · <kbd>Alt</kbd> + clic sur un point : supprimer
          </p>
        </div>
      )}
    </div>
  );
}

/** Exporté pour la barre d'état (D.944) : un seul vocabulaire d'accrochage. */
export function libelleAccroche(n: 'sommet' | 'segment' | 'intersection'): string {
  if (n === 'sommet') return 'un sommet voisin';
  if (n === 'intersection') return 'une intersection';
  return 'un côté voisin';
}

function classeVerdict(e: EtatTrace): string {
  if (!e.deposable || e.verdict === null) return 'attente';
  if (e.verdict.acceptable === null) return 'inconnu';
  return e.verdict.acceptable ? 'acceptable' : 'refuse';
}
